import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { PaperPlaneRight, Image, X, ArrowLeft } from '@phosphor-icons/react';
import { useMBList } from '../hooks/useMBList';

const API = process.env.REACT_APP_BACKEND_URL;

const BroadcastPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [messageImage, setMessageImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('ALL'); // ALL | NONE | MB7..MBn

  const MB_SEGMENTS = useMBList();

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchClients();
  }, [user, navigate]);

  const fetchClients = async () => {
    try {
      // Get all customers
      const response = await axios.get(`${API}/api/users/all-customers`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };

  const toggleClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMessageImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const sendBroadcast = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }

    if (!messageText && !messageImage) {
      toast.error('Please enter a message or select an image');
      return;
    }

    setLoading(true);

    try {
      let imageUrl = null;

      // Upload image if exists
      if (messageImage) {
        const formData = new FormData();
        formData.append('file', messageImage);
        formData.append('media_type', 'image');

        const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

        imageUrl = uploadResponse.data.url;
      }

      // Send broadcast
      await axios.post(`${API}/api/broadcast/send`, {
          sender_id: user.id,
          recipient_ids: selectedClients,
          text: messageText || null,
          image_url: imageUrl
        });

      toast.success(`Broadcast sent to ${selectedClients.length} client${selectedClients.length > 1 ? 's' : ''}!`);
      
      // Reset
      setMessageText('');
      setMessageImage(null);
      setImagePreview(null);
      setSelectedClients([]);

    } catch (error) {
      console.error('Broadcast failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send broadcast';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesQuery =
      client.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.phone?.includes(searchQuery);
    if (!matchesQuery) return false;
    if (segmentFilter === 'ALL') return true;
    if (segmentFilter === 'NONE') return !client.mb_setup;
    return client.mb_setup === segmentFilter;
  });

  const segmentCount = (seg) => {
    if (seg === 'ALL') return clients.length;
    if (seg === 'NONE') return clients.filter(c => !c.mb_setup).length;
    return clients.filter(c => c.mb_setup === seg).length;
  };

  return (
    <div className="h-screen bg-gray-100 overflow-y-auto" style={{ paddingBottom: '50px' }}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                // Session-47: honour explicit `from` state (Settings entry)
                const from = location?.state?.from;
                if (from) return navigate(from, { replace: true });
                navigate(user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard');
              }}
              className="hover:bg-gray-100 p-2 rounded-full transition-colors"
            >
              <ArrowLeft size={24} weight="bold" className="text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Broadcast Message</h1>
              <p className="text-gray-600 mt-1">Send message to multiple clients at once</p>
            </div>
          </div>
        </div>

        {/* Client Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Select Recipients</h2>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {selectedClients.length === filteredClients.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* MB Segment Quick Filter Chips */}
          <div className="flex flex-wrap gap-2 mb-3" data-testid="broadcast-segment-filters">
            {[
              { key: 'ALL', label: 'All Customers' },
              { key: 'NONE', label: 'No Segment' },
              ...MB_SEGMENTS.map(s => ({ key: s, label: s })),
            ].map(({ key, label }) => {
              const active = segmentFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => {
                    setSegmentFilter(key);
                    setSelectedClients([]); // reset selection on filter change
                  }}
                  data-testid={`broadcast-segment-${key.toLowerCase()}-btn`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {label} <span className={active ? 'text-blue-100' : 'text-gray-400'}>({segmentCount(key)})</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="broadcast-search-input"
            className="w-full px-4 py-2 border rounded-lg mb-4"
          />

          {/* Selected Count */}
          <div className="mb-4 text-sm text-gray-600">
            {selectedClients.length} client(s) selected
          </div>

          {/* Client List */}
          <div className="max-h-64 overflow-y-auto border rounded-lg">
            {filteredClients.map(client => (
              <label
                key={client.id}
                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={() => toggleClient(client.id)}
                  className="mr-3 w-4 h-4"
                />
                <div>
                  <div className="font-medium">{client.username}</div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <span>{client.phone}</span>
                    {client.mb_setup && (
                      <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-semibold">
                        {client.mb_setup}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Message Compose */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Compose Message</h2>

          {/* Image Upload */}
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer text-blue-600 hover:text-blue-800">
              <Image size={24} />
              <span>Add Image (Optional)</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </label>

            {imagePreview && (
              <div className="mt-4 relative inline-block">
                <img src={imagePreview} alt="Preview" className="max-w-xs rounded-lg shadow-md" />
                <button
                  onClick={() => {
                    setMessageImage(null);
                    setImagePreview(null);
                  }}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
                  title="Remove image"
                >
                  <X size={16} weight="bold" />
                </button>
                <div className="mt-2 text-xs text-gray-500 italic">
                  ✓ Image added. You can also add a caption below.
                </div>
              </div>
            )}
          </div>

          {/* Text Message */}
          <div className="mb-1">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              {imagePreview ? 'Caption (Optional)' : 'Message Text'}
            </label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={imagePreview ? "Add a caption to your image..." : "Type your message here..."}
              className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="4"
            />
          </div>
          
          {/* Helper Text */}
          <div className="text-xs text-gray-500 mb-4">
            {imagePreview && messageText ? (
              <span className="text-green-600 font-medium">✓ Image with caption will be sent</span>
            ) : imagePreview ? (
              <span>Image will be sent without caption</span>
            ) : messageText ? (
              <span>Text message will be sent</span>
            ) : (
              <span>Add image and/or text to send broadcast</span>
            )}
          </div>

          {/* Send Button */}
          <button
            onClick={sendBroadcast}
            disabled={loading || selectedClients.length === 0}
            className="mt-2 w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
          >
            <PaperPlaneRight size={20} weight="bold" />
            {loading ? 'Sending...' : `Send to ${selectedClients.length} Client(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BroadcastPage;
